/**
 * The module's learner effort, one breakdown per version and modality.
 *
 * @module components/studio/panels/EffortPanel
 */

import React from "react";

import {
  DEFAULT_EFFORT_DATA,
  type EffortData,
  effortTone,
  totalEffortHours,
  type VersionModality,
} from "@/components/shared/effort-hours";
import { EFFORT_FIELDS } from "@/components/studio/module-editor-model";
import type { ModuleEditorState } from "@/components/studio/useModuleEditor";
import { Alert, Badge, Heading, MutedText, Panel } from "@/components/ui";
import { FormField, FormGrid, TextField } from "@/components/ui/Form";
import type { Module } from "@/types";

export function EffortPanel({
  module,
  modalities,
  hoursPerCredit,
  creditUnitLabel,
  updateEffort,
}: {
  module: Module;
  modalities: readonly VersionModality[];
  hoursPerCredit: number;
  creditUnitLabel: string;
  updateEffort: ModuleEditorState["updateEffort"];
}): React.ReactElement {
  const expected = Number(module.credits ?? 0) * hoursPerCredit;

  return (
    <Panel
      aria-label="Effort hours"
      description="Set the learner effort breakdown for each programme version and delivery modality."
    >
      {modalities.length === 0 ? (
        <Alert tone="info">No programme version with a delivery modality is available yet.</Alert>
      ) : (
        <div className="divide-y divide-base-300">
          {modalities.map((modality) => {
            const effort = (module.effortHours?.[modality.key] ?? {}) as Partial<EffortData>;
            const total = totalEffortHours(effort);
            return (
              <fieldset key={modality.key} className="py-6">
                <legend className="sr-only">Effort hours for {modality.label}</legend>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <Heading level={4} size="md">
                      {modality.label}
                    </Heading>
                    <MutedText>
                      {module.credits
                        ? `${module.credits} ${creditUnitLabel} × ${hoursPerCredit} hours`
                        : "Set this module's credits to work out the expected hours."}
                    </MutedText>
                  </div>
                  <Badge
                    tone={module.credits ? effortTone(total, expected) : "neutral"}
                    variant="soft"
                    data-testid={`module-effort-total-${modality.key}`}
                  >
                    {module.credits ? `${total} / ${expected} hours` : `${total} hours`}
                  </Badge>
                </div>
                <FormGrid columns={4} className="mt-5">
                  {EFFORT_FIELDS.map((field) => (
                    <FormField
                      key={field.key}
                      label={field.label}
                      htmlFor={`module-effort-${modality.key}-${field.key}`}
                      helpText={`Enter ${field.label.toLowerCase()} for ${modality.label}.`}
                    >
                      <TextField
                        id={`module-effort-${modality.key}-${field.key}`}
                        type={field.type}
                        min={field.type === "number" ? 0 : undefined}
                        value={effort[field.key] ?? DEFAULT_EFFORT_DATA[field.key]}
                        placeholder={field.placeholder}
                        onChange={(value) =>
                          updateEffort(
                            modality.key,
                            field.key,
                            field.type === "number" ? Math.max(0, Number(value) || 0) : value,
                          )
                        }
                        data-testid={`module-effort-${modality.key}-${field.key}`}
                      />
                    </FormField>
                  ))}
                </FormGrid>
              </fieldset>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

export default EffortPanel;
